<script>
/**
 * SettingsWhatsappBaileys.vue
 * Componente para configurações e conexão WhatsApp Baileys
 * Baseado no BaileysConnectionModal que funciona corretamente
 */
import { mapGetters } from 'vuex';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { useAlert } from 'dashboard/composables';
import InboxesAPI from 'dashboard/api/inboxes';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import BaileysAttributeItem from './BaileysAttributeItem.vue';
import BaileysSettingsSections from 'dashboard/components-next/inbox/BaileysSettingsSections.vue';
import BaileysCredsImport from 'dashboard/components-next/inbox/BaileysCredsImport.vue';
import BaileysPairingCode from 'dashboard/components-next/inbox/BaileysPairingCode.vue';
import BaileysConnectMethodPicker from 'dashboard/components-next/inbox/BaileysConnectMethodPicker.vue';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'dashboard/components-shadcn/new-york/ui/tabs';

export default {
  name: 'SettingsWhatsappBaileys',
  components: {
    NextButton,
    Spinner,
    BaileysAttributeItem,
    BaileysSettingsSections,
    BaileysCredsImport,
    BaileysPairingCode,
    BaileysConnectMethodPicker,
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
  },
  props: {
    inbox: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      activeTab: 'config',
      isLoading: true,
      isSaving: false,
      qrCodeData: null,
      isLoadingQR: false,
      isCheckingStatus: false,
      isConnecting: false,
      isDisconnecting: false,
      errorMessage: null,
      maxImportDays: 30,
      // Como reconectar o ZNew: 'qr' | 'code'. O legado só tem QR.
      connectMethod: 'qr',
      isPreparingCode: false,
      // Configurações do Baileys
      baileysSettings: {
        rejectCall: false,
        msgCall: '',
        receiveGroups: false,
        alwaysOnline: false,
        readMessages: false,
        importMessageHistory: false,
        historyDaysLimit: 7,
        readStatus: false,
        signMsg: false,
        importGroupHistory: false,
        reuseMedia: false,
      },
    };
  },
  computed: {
    // Caixa do WhatsApp Cortex ZNew: tela de opções em seções. O Baileys legado segue com a lista de sempre.
    isZnew() {
      return this.inbox?.provider_config?.gateway === 'znew';
    },
    // Lista do legado (igual ao main; só o rótulo do readStatus corrigido).
    legacyConfigAttributes() {
      return [
        { key: 'rejectCall', label: 'Rejeitar todas as chamadas recebidas', icon: '📵', type: 'checkbox', hasMessage: true },
        { key: 'receiveGroups', label: 'Ignorar todas as mensagens de grupos', icon: '👥', type: 'checkbox' },
        { key: 'alwaysOnline', label: 'Mantenha o WhatsApp sempre online', icon: '🟢', type: 'checkbox' },
        { key: 'readMessages', label: 'Marcar todas as mensagens como lidas', icon: '✅', type: 'checkbox' },
        { key: 'importMessageHistory', label: 'Sincronizar historico ao escanear QR Code', icon: '🔄', type: 'checkbox' },
        { key: 'historyDaysLimit', label: 'Limite de dias para importacao', icon: '📅', type: 'number', min: 1, max: this.maxImportDays, showIf: 'importMessageHistory' },
        { key: 'readStatus', label: 'Receber os status (stories) dos contatos', icon: '📸', type: 'checkbox' },
        { key: 'signMsg', label: 'Assinar mensagem com nome de usuario Cortexwoot', icon: '✍️', type: 'checkbox' },
      ];
    },
    ...mapGetters({
      getBaileysConnectionStatus: 'inboxes/getBaileysConnectionStatus',
    }),
    connectionStatus() {
      return this.getBaileysConnectionStatus(this.inbox.id);
    },
    statusConfig() {
      const rawStatus = this.connectionStatus?.status || '';
      const status = typeof rawStatus === 'string' ? rawStatus.toUpperCase() : '';

      const configs = {
        OPEN: {
          label: 'Conectado',
          color: 'bg-n-teal-9',
          textColor: 'text-n-teal-11',
          bgColor: 'bg-n-teal-3',
          borderColor: 'border-n-teal-6',
        },
        CONNECTED: {
          label: 'Conectado',
          color: 'bg-n-teal-9',
          textColor: 'text-n-teal-11',
          bgColor: 'bg-n-teal-3',
          borderColor: 'border-n-teal-6',
        },
        CONNECTING: {
          label: 'Conectando...',
          color: 'bg-n-amber-9',
          textColor: 'text-n-amber-11',
          bgColor: 'bg-n-amber-3',
          borderColor: 'border-n-amber-6',
        },
        CLOSE: {
          label: 'Desconectado',
          color: 'bg-n-ruby-9',
          textColor: 'text-n-ruby-11',
          bgColor: 'bg-n-ruby-3',
          borderColor: 'border-n-ruby-6',
        },
        DISCONNECTED: {
          label: 'Desconectado',
          color: 'bg-n-ruby-9',
          textColor: 'text-n-ruby-11',
          bgColor: 'bg-n-ruby-3',
          borderColor: 'border-n-ruby-6',
        },
        NOT_FOUND: {
          label: 'Instancia nao encontrada',
          color: 'bg-n-amber-9',
          textColor: 'text-n-amber-11',
          bgColor: 'bg-n-amber-3',
          borderColor: 'border-n-amber-6',
        },
        ERROR: {
          label: 'Erro na conexao',
          color: 'bg-n-ruby-9',
          textColor: 'text-n-ruby-11',
          bgColor: 'bg-n-ruby-3',
          borderColor: 'border-n-ruby-6',
        },
      };

      return configs[status] || {
        label: 'Verificando...',
        color: 'bg-n-slate-9',
        textColor: 'text-n-slate-11',
        bgColor: 'bg-n-slate-3',
        borderColor: 'border-n-slate-6',
      };
    },
    isConnected() {
      const status = (this.connectionStatus?.status || '').toUpperCase();
      return status === 'OPEN' || status === 'CONNECTED';
    },
    phoneNumber() {
      return this.connectionStatus?.phone_number || this.connectionStatus?.phoneNumber || this.inbox.phone_number;
    },
  },
  watch: {
    // Conectou, venha de onde vier (evento, "Atualizar", tela do código): volta pro QR. Ficar em "código"
    // remontava o BaileysPairingCode quando caísse, e ele pede o código na montagem — o gateway abre um
    // pareamento que ninguém pediu. O QR só gera no clique em "Conectar".
    isConnected(connected) {
      if (connected) this.connectMethod = 'qr';
    },
  },
  mounted() {
    this.loadData();

    // Escutar eventos WebSocket
    emitter.on(BUS_EVENTS.BAILEYS_CONNECTED, this.handleBaileysConnected);
    emitter.on(BUS_EVENTS.BAILEYS_DISCONNECTED, this.handleBaileysDisconnected);
    emitter.on(BUS_EVENTS.BAILEYS_QR_GENERATED, this.handleBaileysQRGenerated);
  },
  beforeUnmount() {
    emitter.off(BUS_EVENTS.BAILEYS_CONNECTED, this.handleBaileysConnected);
    emitter.off(BUS_EVENTS.BAILEYS_DISCONNECTED, this.handleBaileysDisconnected);
    emitter.off(BUS_EVENTS.BAILEYS_QR_GENERATED, this.handleBaileysQRGenerated);
  },
  methods: {
    // ===== WEBSOCKET HANDLERS =====
    handleBaileysConnected(data) {
      if (data.inbox_id !== this.inbox.id) return;
      this.qrCodeData = null;
      useAlert('WhatsApp conectado com sucesso!');
    },
    handleBaileysDisconnected(data) {
      if (data.inbox_id !== this.inbox.id) return;
      this.qrCodeData = null;
    },
    handleBaileysQRGenerated(data) {
      if (Number(data.inbox_id) !== this.inbox.id) return;
      if (this.connectMethod === 'qr') this.fetchQRCode();
    },

    // ===== DATA LOADING =====
    async loadData() {
      this.isLoading = true;
      try {
        await this.checkStatus();
      } finally {
        this.isLoading = false;
      }
    },

    async checkStatus() {
      if (!this.inbox.id) return;

      this.isCheckingStatus = true;
      this.errorMessage = null;

      try {
        const response = await InboxesAPI.baileysGetStatus(this.inbox.id);

        if (response.data?.success) {
          // Atualizar store com status
          this.$store.dispatch('inboxes/setBaileysConnectionStatus', {
            inboxId: this.inbox.id,
            status: response.data.data,
          });

          // Carregar configurações
          if (response.data.data?.settings) {
            this.loadSettings(response.data.data.settings);
          }

          // Atualizar max_import_days
          if (response.data.data?.config?.max_import_days) {
            this.maxImportDays = response.data.data.config.max_import_days;
          }
        } else {
          this.$store.dispatch('inboxes/setBaileysConnectionStatus', {
            inboxId: this.inbox.id,
            status: { status: 'error' },
          });
        }
      } catch (error) {
        console.error('[SettingsWhatsappBaileys] Error checking status:', error);
        const status = error.response?.status === 404
          ? { status: 'not_found' }
          : { status: 'error' };
        this.$store.dispatch('inboxes/setBaileysConnectionStatus', {
          inboxId: this.inbox.id,
          status,
        });
      } finally {
        this.isCheckingStatus = false;
      }
    },

    loadSettings(settings) {
      this.baileysSettings = {
        rejectCall: settings.reject_call || false,
        msgCall: settings.msg_call || '',
        receiveGroups: settings.receive_groups || false,
        alwaysOnline: settings.always_online || false,
        readMessages: settings.read_messages || false,
        importMessageHistory: settings.import_message_history || false,
        historyDaysLimit: settings.history_days_limit || 7,
        readStatus: settings.read_status || false,
        signMsg: settings.sign_msg || false,
        importGroupHistory: settings.import_group_history || false,
        reuseMedia: settings.reuse_media || false,
      };
    },

    // ===== QR CODE =====
    async fetchQRCode() {
      if (!this.inbox.id) return;

      this.isLoadingQR = true;
      this.errorMessage = null;

      try {
        const response = await InboxesAPI.baileysGetQRCode(this.inbox.id);

        if (response.data?.success) {
          // Atualizar status se veio na resposta
          if (response.data.status) {
            this.$store.dispatch('inboxes/setBaileysConnectionStatus', {
              inboxId: this.inbox.id,
              status: response.data.status,
            });
          }

          if (this.isConnected) {
            useAlert('WhatsApp ja esta conectado!');
            this.qrCodeData = null;
            return;
          }

          const qrBase64 = response.data.qrcode?.base64;

          if (qrBase64 && qrBase64.trim() !== '') {
            this.qrCodeData = qrBase64;
          } else {
            this.errorMessage = 'QR Code nao disponivel';
            this.qrCodeData = null;
          }
        } else {
          this.errorMessage = response.data?.error || 'Erro ao obter QR Code';
        }
      } catch (error) {
        console.error('[SettingsWhatsappBaileys] Error fetching QR:', error);
        this.errorMessage = error.response?.data?.error || 'Erro ao obter QR Code';
      } finally {
        this.isLoadingQR = false;
      }
    },

    // ===== CONNECTION =====
    // Código de pareamento (só ZNew): a instância tem que existir no gateway antes de pedir o código.
    async selectConnectMethod(method) {
      if (this.isPreparingCode || this.isCheckingStatus) return;
      this.connectMethod = method;
      this.errorMessage = null;
      if (method !== 'code') return;

      const status = (this.connectionStatus?.status || '').toUpperCase();
      if (status !== 'NOT_FOUND') return;

      this.isPreparingCode = true;
      try {
        await InboxesAPI.baileysCreateInstance(this.inbox.id);
      } catch (error) {
        this.errorMessage =
          error.response?.data?.error ||
          this.$t('INBOX_MGMT.BAILEYS.CONNECTION.CREATE_INSTANCE_ERROR');
        this.connectMethod = 'qr';
      } finally {
        this.isPreparingCode = false;
      }
    },

    setPairingStatus(status) {
      this.$store.dispatch('inboxes/setBaileysConnectionStatus', {
        inboxId: this.inbox.id,
        status,
      });
    },

    async connectInstance() {
      if (!this.inbox.id) return;

      this.isConnecting = true;
      this.errorMessage = null;

      try {
        await this.checkStatus();

        if (this.isConnected) {
          useAlert('WhatsApp ja esta conectado!');
          this.isConnecting = false;
          return;
        }

        // Criar instancia se necessario
        try {
          await InboxesAPI.baileysCreateInstance(this.inbox.id);
        } catch (e) {
          console.warn('[SettingsWhatsappBaileys] Instance may already exist');
        }

        await InboxesAPI.baileysConnect(this.inbox.id);
        await this.fetchQRCode();

      } catch (error) {
        console.error('[SettingsWhatsappBaileys] Error connecting:', error);
        this.errorMessage = error.response?.data?.error || 'Erro ao conectar';
        useAlert(this.errorMessage);
      } finally {
        this.isConnecting = false;
      }
    },

    async disconnectInstance() {
      if (!this.inbox.id) return;

      this.isDisconnecting = true;
      this.errorMessage = null;

      try {
        await InboxesAPI.baileysDisconnect(this.inbox.id);

        this.$store.dispatch('inboxes/setBaileysConnectionStatus', {
          inboxId: this.inbox.id,
          status: { status: 'DISCONNECTED' },
        });
        this.qrCodeData = null;

        useAlert('WhatsApp desconectado com sucesso');
      } catch (error) {
        console.error('[SettingsWhatsappBaileys] Error disconnecting:', error);
        this.errorMessage = error.response?.data?.error || 'Erro ao desconectar';
        useAlert(this.errorMessage);
      } finally {
        this.isDisconnecting = false;
      }
    },

    // ===== SETTINGS =====
    // Mapeamento de keys do frontend para backend
    getKeyMap() {
      return {
        rejectCall: 'reject_call',
        msgCall: 'msg_call',
        receiveGroups: 'receive_groups',
        alwaysOnline: 'always_online',
        readMessages: 'read_messages',
        importMessageHistory: 'import_message_history',
        historyDaysLimit: 'history_days_limit',
        readStatus: 'read_status',
        signMsg: 'sign_msg',
        importGroupHistory: 'import_group_history',
        reuseMedia: 'reuse_media',
      };
    },

    async handleAttributeChange({ key, value }) {
      this.isSaving = true;

      const keyMap = this.getKeyMap();
      const apiKey = keyMap[key];
      const payload = { [apiKey]: value };

      // Se for rejectCall e tiver msgCall, incluir
      if (key === 'rejectCall' && this.baileysSettings.msgCall) {
        payload.msg_call = this.baileysSettings.msgCall;
      }

      // Se for importMessageHistory ativado, incluir o historyDaysLimit com valor padrao
      if (key === 'importMessageHistory' && value === true) {
        payload.history_days_limit = this.baileysSettings.historyDaysLimit || 7;
      }


      try {
        await InboxesAPI.baileysUpdateConfig(this.inbox.id, payload);
        useAlert('Configuracao salva com sucesso!');
      } catch (error) {
        console.error('[SettingsWhatsappBaileys] Error saving:', error);
        useAlert(error.message || 'Erro ao salvar configuracao');
        // Reverter valor
        this.baileysSettings[key] = !value;
      } finally {
        this.isSaving = false;
      }
    },

    async saveMsgCall() {
      this.isSaving = true;

      try {
        await InboxesAPI.baileysUpdateConfig(this.inbox.id, {
          reject_call: this.baileysSettings.rejectCall,
          msg_call: this.baileysSettings.msgCall,
        });
        useAlert('Mensagem de rejeicao salva!');
      } catch (error) {
        console.error('[SettingsWhatsappBaileys] Error saving msg_call:', error);
        useAlert(error.message || 'Erro ao salvar mensagem');
      } finally {
        this.isSaving = false;
      }
    },

    async saveDaysLimit() {
      const validDays = Math.min(Math.max(1, this.baileysSettings.historyDaysLimit), this.maxImportDays);
      this.baileysSettings.historyDaysLimit = validDays;
      this.isSaving = true;

      try {
        await InboxesAPI.baileysUpdateConfig(this.inbox.id, {
          history_days_limit: validDays,
        });
        useAlert('Limite de dias salvo!');
      } catch (error) {
        console.error('[SettingsWhatsappBaileys] Error saving days limit:', error);
        useAlert(error.message || 'Erro ao salvar limite');
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<template>
  <div class="settings-baileys">
    <!-- Loading -->
    <div v-if="isLoading" class="flex justify-center items-center p-8">
      <Spinner size="32" class="text-woot-600" />
    </div>

    <!-- Content -->
    <div v-else>
      <!-- Tabs usando shadcn/ui -->
      <Tabs v-model="activeTab" class="tabs-custom w-full">
        <TabsList class="tabs-list-custom">
          <TabsTrigger value="config" class="tabs-trigger-custom">
            Configuracao
          </TabsTrigger>
          <TabsTrigger value="qrcode" class="tabs-trigger-custom">
            <template v-if="isZnew">
              {{ $t('INBOX_MGMT.BAILEYS.CONNECTION.ZNEW_TAB') }}
            </template>
            <template v-else>QR Code</template>
          </TabsTrigger>
        </TabsList>

        <!-- Tab: Configuracao -->
        <TabsContent value="config" class="tabs-content-custom">
          <BaileysSettingsSections
            v-if="isZnew"
            :settings="baileysSettings"
            :max-import-days="maxImportDays"
            :disabled="isSaving"
            @update="({ key, value }) => (baileysSettings[key] = value)"
            @change="handleAttributeChange"
            @blur-msg="saveMsgCall"
            @blur-number="saveDaysLimit"
          />
          <div v-else class="space-y-1">
            <template v-for="attr in legacyConfigAttributes" :key="attr.key">
              <!-- Mostrar apenas se nao tem condicao ou se a condicao e verdadeira -->
              <BaileysAttributeItem
                v-if="!attr.showIf || baileysSettings[attr.showIf]"
                :attribute="attr"
                v-model="baileysSettings[attr.key]"
                v-model:msg-call-value="baileysSettings.msgCall"
                :disabled="isSaving"
                @change="handleAttributeChange"
                @blur-msg="saveMsgCall"
                @blur-number="saveDaysLimit"
              />
            </template>
          </div>
        </TabsContent>

        <!-- Tab: QR Code -->
        <TabsContent value="qrcode" class="tabs-content-custom">
        <!-- Status Card -->
        <div
          class="p-3 mb-4 rounded-lg border flex items-center justify-between"
          :class="[statusConfig.bgColor, statusConfig.borderColor]"
        >
          <div class="flex items-center gap-3">
            <span
              class="w-3 h-3 rounded-full"
              :class="[statusConfig.color, { 'animate-pulse': !isConnected }]"
            />
            <div>
              <p class="font-medium text-sm" :class="statusConfig.textColor">
                {{ statusConfig.label }}
              </p>
              <p v-if="phoneNumber" class="text-xs text-n-slate-11">
                {{ phoneNumber }}
              </p>
            </div>
          </div>
          <NextButton
            :is-loading="isCheckingStatus"
            size="xs"
            ghost
            label="Atualizar"
            @click="checkStatus"
          />
        </div>

        <!-- Error Message -->
        <div
          v-if="errorMessage"
          class="p-3 mb-4 rounded-lg border bg-n-ruby-3 border-n-ruby-6"
        >
          <p class="text-sm text-n-ruby-11">{{ errorMessage }}</p>
        </div>

        <!-- Desconectado: ZNew escolhe QR ou código; legado só QR -->
        <div v-if="!isConnected" class="flex flex-col items-center">
          <BaileysConnectMethodPicker
            v-if="isZnew"
            :model-value="connectMethod"
            :disabled="isPreparingCode || isCheckingStatus"
            class="w-full mb-6"
            @update:model-value="selectConnectMethod"
          />

          <div v-if="isPreparingCode" class="flex justify-center p-8">
            <Spinner :size="32" class="text-n-brand" />
          </div>

          <BaileysPairingCode
            v-else-if="isZnew && connectMethod === 'code'"
            class="w-full"
            :inbox-id="inbox.id"
            :phone-number="phoneNumber"
            @status="setPairingStatus"
            @use-qr="selectConnectMethod('qr')"
          />

          <template v-else>
            <!-- QR Code Display -->
            <div
              class="p-4 mb-4 rounded-lg bg-white dark:bg-n-slate-1"
              :class="{ 'animate-pulse': isLoadingQR }"
            >
              <img
                v-if="qrCodeData"
                :src="qrCodeData"
                alt="QR Code WhatsApp"
                class="w-56 h-56"
              />
              <div
                v-else
                class="flex items-center justify-center w-56 h-56 rounded bg-n-slate-3"
              >
                <span v-if="isLoadingQR" class="text-n-slate-11 text-sm">
                  Carregando QR Code...
                </span>
                <span v-else class="text-n-slate-11 text-center text-sm px-4">
                  Clique em 'Conectar' para gerar o QR Code
                </span>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="flex gap-3 mb-4">
              <NextButton
                v-if="!qrCodeData"
                :is-loading="isConnecting"
                solid
                blue
                label="Conectar"
                @click="connectInstance"
              />
              <template v-else>
                <NextButton
                  :is-loading="isLoadingQR"
                  solid
                  label="Atualizar QR"
                  @click="fetchQRCode"
                />
                <NextButton
                  :is-loading="isCheckingStatus"
                  solid
                  blue
                  label="Verificar Conexao"
                  @click="checkStatus"
                />
              </template>
            </div>

            <!-- Instructions -->
            <div class="w-full p-4 rounded-lg bg-n-slate-3">
              <h4 class="mb-2 font-medium text-sm text-n-slate-12">
                Como conectar:
              </h4>
              <ol class="list-decimal list-inside text-xs text-n-slate-11 space-y-1">
                <li>Abra o WhatsApp no seu celular</li>
                <li>Toque em Menu ou Configuracoes e selecione 'Aparelhos conectados'</li>
                <li>Toque em 'Conectar um aparelho'</li>
                <li>Aponte seu celular para esta tela para escanear o QR Code</li>
              </ol>
            </div>
          </template>

          <!-- Import de credenciais (contorno da passkey) -->
          <div class="w-full mt-4">
            <BaileysCredsImport
              :inbox-id="inbox.id"
              @imported="checkStatus"
            />
          </div>
        </div>

        <!-- Connected State -->
        <div v-else class="flex flex-col items-center py-4">
          <div class="w-16 h-16 rounded-full bg-n-teal-3 flex items-center justify-center mb-3">
            <svg class="w-8 h-8 text-n-teal-11" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 class="text-base font-medium text-n-slate-12 mb-1">
            WhatsApp Conectado
          </h3>
          <p class="text-sm text-n-slate-11 mb-4">
            Sua instancia esta pronta para enviar e receber mensagens.
          </p>
          <!-- Disconnect Button -->
          <NextButton
            :is-loading="isDisconnecting"
            ruby
            label="Desconectar"
            @click="disconnectInstance"
          />
        </div>
        </TabsContent>
      </Tabs>
    </div>
  </div>
</template>

<style scoped>
.settings-baileys {
  min-height: 300px;
}

/* Tabs Container */
:deep(.tabs-list-custom) {
  @apply grid w-full grid-cols-2 bg-woot-25 dark:bg-slate-800;
  @apply border border-slate-200 dark:border-slate-700 rounded-lg p-1;
}

/* Tab Triggers */
:deep(.tabs-trigger-custom) {
  @apply flex items-center justify-center px-3 py-2 text-sm font-medium;
  @apply rounded-md transition-all duration-200;
  @apply text-slate-600 dark:text-slate-400;
  @apply hover:text-slate-900 dark:hover:text-slate-100;
  @apply hover:bg-slate-100 dark:hover:bg-slate-700;
}

:deep(.tabs-trigger-custom[data-state="active"]) {
  @apply bg-white dark:bg-slate-900 text-woot-600 dark:text-woot-400;
  @apply border border-slate-200 dark:border-slate-600;
  @apply shadow-sm;
}

/* Tab Content */
:deep(.tabs-content-custom) {
  @apply mt-4 p-6 bg-white dark:bg-slate-900;
  @apply border border-slate-200 dark:border-slate-700 rounded-lg;
  min-height: 360px;
}

/* Focus states */
:deep(.tabs-trigger-custom:focus-visible) {
  @apply outline-2 outline-offset-2 outline-woot-500;
}
</style>
